/**
 * Persistance locale des make-up_session (weakness_reinforcement.md + v2).
 * Clés JSON avec tirets : 'make-up_session_id', 'make-up_questions_nb'.
 */

const storageKey = (studentId) => `makeup_sessions_${studentId || 'anonymous_user'}`;

/** Plafond make-up par liste de mots-clés (erreur : départ 2, +1 par tentative, max 3). */
export const MAKEUP_MAX_PER_KEYWORD_LIST = 3;
export const MAKEUP_INITIAL_WRONG_NB = 2;

export function generateMakeupSessionId() {
    const n = Math.floor(1000000 + Math.random() * 9000000);
    return String(n);
}

export function buildKeywordsTriplet(currentQCM, correctAnswers) {
    const raw =
        (currentQCM && (currentQCM.keywords || currentQCM.qcm?.keywords)) || [];
    const asStrings = (Array.isArray(raw) ? raw : []).map((k) => String(k).trim()).filter(Boolean);

    if (asStrings.length >= 3) {
        return asStrings.slice(0, 3);
    }

    const wordsFromAnswers = [];
    const answers = Array.isArray(correctAnswers) ? correctAnswers : [];
    for (const ca of answers) {
        const text = (ca && ca.answer) || '';
        const words = text
            .split(/\s+/)
            .map((w) => w.replace(/[^\w-]/g, ''))
            .filter((w) => w.length > 2);
        wordsFromAnswers.push(...words);
    }

    const merged = [...asStrings];
    for (const w of wordsFromAnswers) {
        if (merged.length >= 3) break;
        const lower = w.toLowerCase();
        if (!merged.some((m) => m.toLowerCase() === lower)) {
            merged.push(w);
        }
    }

    while (merged.length < 3) {
        merged.push('practice');
    }
    return merged.slice(0, 3);
}

/**
 * Mots-clés pour une option cliquée : option.keywords si présent, sinon dérivation du texte + QCM.
 */
export function buildKeywordsForOption(option, currentQCM, correctAnswers) {
    const rawOpt = option && Array.isArray(option.keywords) ? option.keywords : [];
    const fromOpt = rawOpt.map((k) => String(k).trim()).filter(Boolean);
    const qcmFlat = buildKeywordsTriplet(currentQCM, correctAnswers);

    if (fromOpt.length > 0) {
        const triple = [...fromOpt];
        for (const w of qcmFlat) {
            if (triple.length >= 3) break;
            const lower = w.toLowerCase();
            if (!triple.some((t) => String(t).toLowerCase() === lower)) {
                triple.push(w);
            }
        }
        while (triple.length < 3) triple.push('practice');
        return triple.slice(0, 3);
    }

    const text = (option && option.answer) || '';
    const words = text
        .split(/\s+/)
        .map((w) => w.replace(/[^\w-]/g, ''))
        .filter((w) => w.length > 2);
    const merged = [];
    for (const w of words) {
        const lower = w.toLowerCase();
        if (!merged.some((m) => m.toLowerCase() === lower)) merged.push(w);
        if (merged.length >= 3) break;
    }
    for (const w of qcmFlat) {
        if (merged.length >= 3) break;
        const lower = w.toLowerCase();
        if (!merged.some((m) => m.toLowerCase() === lower)) merged.push(w);
    }
    while (merged.length < 3) merged.push('practice');
    return merged.slice(0, 3);
}

/** Matrice v2 : N = nombre de questions make-up pour ce clic (0 = pas de session). Réservé aux réponses correctes ; les erreurs passent par upsertMakeupSessionOnWrongByKeywordList. */
export function getMakeupQuestionsNbForInteraction(isCorrect, certainty) {
    if (!isCorrect) {
        return 0;
    }
    const level =
        certainty === 'high' || certainty === 'medium' || certainty === 'low' ? certainty : 'medium';
    const map = { high: 0, medium: 1, low: 2 };
    return map[level];
}

function normalizedKeywordKey(keywords) {
    const arr = (keywords || []).map((k) => String(k).trim().toLowerCase()).filter(Boolean);
    const uniq = [...new Set(arr)];
    uniq.sort((a, b) => a.localeCompare(b));
    return uniq.join('|') || '__empty__';
}

/**
 * Erreur :2 questions make-up pour la liste de mots-clés des bonnes réponses, puis +1 par tentative,
 * plafonné à MAKEUP_MAX_PER_KEYWORD_LIST. Fusion sur la clé normalisée des mots-clés.
 */
export function upsertMakeupSessionOnWrongByKeywordList(
    studentId,
    { sourceQuestionId, keywords, domain, task, examId, sessionId }
) {
    if (!sourceQuestionId) return null;
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid);
    const triple = (keywords || []).slice(0, 3);
    while (triple.length < 3) triple.push('practice');
    const key = normalizedKeywordKey(triple);

    const existingIdx = sessions.findIndex((s) => {
        if ((s.content?.['make-up_questions_nb'] || 0) <= 0) return false;
        const kw = s.content?.keywords || [];
        return normalizedKeywordKey(kw) === key;
    });

    if (existingIdx >= 0) {
        const existing = sessions[existingIdx];
        if (existing.content.initial_makeup_questions_nb == null) {
            existing.content.initial_makeup_questions_nb =
                existing.content['make-up_questions_nb'] || MAKEUP_INITIAL_WRONG_NB;
        }
        const cur = Math.min(
            MAKEUP_MAX_PER_KEYWORD_LIST,
            existing.content['make-up_questions_nb'] || 0
        );
        existing.content['make-up_questions_nb'] = Math.min(MAKEUP_MAX_PER_KEYWORD_LIST, cur + 1);
        const meta = existing.metadata || (existing.metadata = {});
        if (examId != null && String(examId).trim() !== '') meta.exam_id = String(examId).trim();
        if (sessionId != null && String(sessionId).trim() !== '') meta.session_id = String(sessionId).trim();
        const prevIds = Array.isArray(meta.source_question_ids) ? meta.source_question_ids : [];
        const merged = new Set(
            [...prevIds, existing.source_question_id, sourceQuestionId].map(String).filter(Boolean)
        );
        meta.source_question_ids = [...merged];
        existing.source_question_id = String(sourceQuestionId);
        saveSessions(sid, sessions);
        return existing;
    }

    const createdAt = new Date().toISOString();
    const session = {
        'make-up_session_id': generateMakeupSessionId(),
        created_at: createdAt,
        student_id: sid,
        source_question_id: String(sourceQuestionId),
        metadata: {
            domain: domain || '',
            task: task || '',
            created_at: createdAt,
            source_question_ids: [String(sourceQuestionId)],
            ...(examId != null && String(examId).trim() !== '' ? { exam_id: String(examId).trim() } : {}),
            ...(sessionId != null && String(sessionId).trim() !== ''
                ? { session_id: String(sessionId).trim() }
                : {}),
        },
        content: {
            keywords: triple,
            'make-up_questions_nb': MAKEUP_INITIAL_WRONG_NB,
            initial_makeup_questions_nb: MAKEUP_INITIAL_WRONG_NB,
        },
    };
    sessions.push(session);
    saveSessions(sid, sessions);
    return session;
}

/**
 * v2 : une session distincte par clic (pas de fusion source_question_id).
 */
export function pushMakeupSessionPerInteraction(
    studentId,
    {
        sourceQuestionId,
        domain,
        task,
        interactionIndex,
        certainty,
        keywords,
        makeupQuestionsNb,
        examId,
        sessionId,
    }
) {
    const n = Math.min(
        MAKEUP_MAX_PER_KEYWORD_LIST,
        Math.max(0, Math.floor(Number(makeupQuestionsNb) || 0))
    );
    if (!sourceQuestionId || n <= 0) return null;
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid);
    const triple = (keywords || []).slice(0, 3);
    while (triple.length < 3) triple.push('practice');

    const createdAt = new Date().toISOString();
    const session = {
        'make-up_session_id': generateMakeupSessionId(),
        created_at: createdAt,
        student_id: sid,
        source_question_id: String(sourceQuestionId),
        metadata: {
            domain: domain || '',
            task: task || '',
            interaction_index: interactionIndex,
            certainty: certainty || '',
            created_at: createdAt,
            ...(examId != null && String(examId).trim() !== '' ? { exam_id: String(examId).trim() } : {}),
            ...(sessionId != null && String(sessionId).trim() !== ''
                ? { session_id: String(sessionId).trim() }
                : {}),
        },
        content: {
            keywords: triple,
            'make-up_questions_nb': n,
            initial_makeup_questions_nb: n,
        },
    };
    sessions.push(session);
    saveSessions(sid, sessions);
    return session;
}

function loadSessions(studentId) {
    try {
        const raw = localStorage.getItem(storageKey(studentId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        let dirty = false;
        for (const s of parsed) {
            const c = s && s.content;
            if (!c) continue;
            const nb = Number(c['make-up_questions_nb'] || 0);
            if (nb > MAKEUP_MAX_PER_KEYWORD_LIST) {
                c['make-up_questions_nb'] = MAKEUP_MAX_PER_KEYWORD_LIST;
                dirty = true;
            }
        }
        if (dirty) {
            localStorage.setItem(storageKey(studentId), JSON.stringify(parsed));
        }
        return parsed;
    } catch {
        return [];
    }
}

/** Export pour dashboard / API (sessions de rattrapage brutes). */
export function loadMakeupSessionsForUser(studentId) {
    return loadSessions(studentId || 'anonymous_user');
}

/**
 * Sessions make-up pour le couple examen + session d’étude courants.
 * Filtrage strict : metadata.exam_id et metadata.session_id doivent matcher le contexte.
 */
export function filterMakeupSessionsByExamAndSession(sessions, examId, sessionId) {
    const ex = String(examId || '').trim();
    const sid = String(sessionId || '').trim();
    if (!ex && !sid) return sessions || [];
    return (sessions || []).filter((s) => {
        const m = s.metadata || {};
        const mex = m.exam_id != null ? String(m.exam_id).trim() : '';
        const msid = m.session_id != null ? String(m.session_id).trim() : '';
        if (ex && mex !== ex) return false;
        if (sid && msid !== sid) return false;
        return true;
    });
}

export function loadMakeupSessionsForExamSession(studentId, examId, sessionId) {
    return filterMakeupSessionsByExamAndSession(
        loadMakeupSessionsForUser(studentId),
        examId,
        sessionId
    );
}

function saveSessions(studentId, sessions) {
    localStorage.setItem(storageKey(studentId), JSON.stringify(sessions));
}

/**
 * Mode normal : clic distracteur — crée une session (nb=2) ou +1 si même source_question_id.
 */
export function upsertMakeupSessionOnWrong(studentId, { sourceQuestionId, keywords, domain, task, examId, sessionId }) {
    if (!sourceQuestionId) return;
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid);
    const triple = (keywords || []).slice(0, 3);
    while (triple.length < 3) triple.push('practice');

    const existing = sessions.find(
        (s) => s.source_question_id === sourceQuestionId && (s.content?.['make-up_questions_nb'] || 0) > 0
    );

    if (existing) {
        if (existing.content.initial_makeup_questions_nb == null) {
            existing.content.initial_makeup_questions_nb =
                existing.content['make-up_questions_nb'] || MAKEUP_INITIAL_WRONG_NB;
        }
        const cur = Math.min(
            MAKEUP_MAX_PER_KEYWORD_LIST,
            existing.content['make-up_questions_nb'] || 0
        );
        existing.content['make-up_questions_nb'] = Math.min(MAKEUP_MAX_PER_KEYWORD_LIST, cur + 1);
        const em = existing.metadata || (existing.metadata = {});
        if (examId != null && String(examId).trim() !== '') em.exam_id = String(examId).trim();
        if (sessionId != null && String(sessionId).trim() !== '') em.session_id = String(sessionId).trim();
        saveSessions(sid, sessions);
        return existing;
    }

    const createdAt = new Date().toISOString();
    const initialNb = MAKEUP_INITIAL_WRONG_NB;
    const session = {
        'make-up_session_id': generateMakeupSessionId(),
        created_at: createdAt,
        student_id: sid,
        source_question_id: String(sourceQuestionId),
        metadata: {
            domain: domain || '',
            task: task || '',
            created_at: createdAt,
            ...(examId != null && String(examId).trim() !== '' ? { exam_id: String(examId).trim() } : {}),
            ...(sessionId != null && String(sessionId).trim() !== ''
                ? { session_id: String(sessionId).trim() }
                : {}),
        },
        content: {
            keywords: triple,
            'make-up_questions_nb': initialNb,
            initial_makeup_questions_nb: initialNb,
        },
    };
    sessions.push(session);
    saveSessions(sid, sessions);
    return session;
}

/** Session avec le plus grand make-up_questions_nb (>0). */
export function pickHighestMakeupSession(studentId) {
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid).filter(
        (s) => (s.content?.['make-up_questions_nb'] || 0) > 0
    );
    if (sessions.length === 0) return null;
    sessions.sort(
        (a, b) =>
            (b.content?.['make-up_questions_nb'] || 0) -
            (a.content?.['make-up_questions_nb'] || 0)
    );
    return sessions[0];
}

export function incrementMakeupSessionNb(studentId, makeupSessionId) {
    if (!makeupSessionId) return;
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid);
    const found = sessions.find((s) => s['make-up_session_id'] === makeupSessionId);
    if (!found) return;
    const cur = Math.min(
        MAKEUP_MAX_PER_KEYWORD_LIST,
        found.content['make-up_questions_nb'] || 0
    );
    found.content['make-up_questions_nb'] = Math.min(MAKEUP_MAX_PER_KEYWORD_LIST, cur + 1);
    saveSessions(sid, sessions);
}

/**
 * Réussite complète en mode renforcement : -1 ; si 0, suppression. Retourne le nouveau nb ou null si supprimée.
 */
export function decrementMakeupSessionNb(studentId, makeupSessionId) {
    if (!makeupSessionId) return null;
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid);
    const idx = sessions.findIndex((s) => s['make-up_session_id'] === makeupSessionId);
    if (idx === -1) return null;

    const nb = (sessions[idx].content['make-up_questions_nb'] || 0) - 1;
    if (nb <= 0) {
        sessions.splice(idx, 1);
        saveSessions(sid, sessions);
        return null;
    }
    sessions[idx].content['make-up_questions_nb'] = nb;
    saveSessions(sid, sessions);
    return nb;
}

/**
 * Après réussite complète d’un QCM : décrémente la session make-up alignée sur exam/session + triplet de mots-clés.
 * @returns {{ applied: boolean }}
 */
export function decrementMakeupSessionForCompletedQcm(studentId, examId, sessionId, { keywords }) {
    const sid = studentId || 'anonymous_user';
    const all = loadSessions(sid);
    const scoped = filterMakeupSessionsByExamAndSession(all, examId, sessionId);
    const triple = (keywords || []).slice(0, 3);
    while (triple.length < 3) triple.push('practice');
    const key = normalizedKeywordKey(triple);
    const target = scoped.find((s) => {
        if ((s.content?.['make-up_questions_nb'] || 0) <= 0) return false;
        const kw = s.content?.keywords || [];
        return normalizedKeywordKey(kw) === key;
    });
    if (!target) {
        return { applied: false };
    }
    const makeupSessionId = target['make-up_session_id'];
    decrementMakeupSessionNb(sid, makeupSessionId);
    return { applied: true };
}

/** Tous les source_question_id des sessions actives (exclusions recherche). */
export function collectAllSourceQuestionIds(studentId) {
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid);
    const ids = new Set();
    for (const s of sessions) {
        if ((s.content?.['make-up_questions_nb'] || 0) <= 0) continue;
        const meta = s.metadata || {};
        if (Array.isArray(meta.source_question_ids)) {
            meta.source_question_ids.forEach((id) => ids.add(String(id)));
        }
        if (s.source_question_id) {
            ids.add(String(s.source_question_id));
        }
    }
    return [...ids];
}

/**
 * Agrège les make-up_session par triplet de mots-clés (spec strategy_apprentissage mode B).
 * scoreSum = somme des make-up_questions_nb ; barScore = min(5, scoreSum).
 */
export function aggregateMakeupConceptsByTriplet(studentId) {
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid).filter(
        (s) => (s.content?.['make-up_questions_nb'] || 0) > 0
    );
    const map = new Map();

    for (const s of sessions) {
        const rawKw = s.content?.keywords || [];
        const norm = rawKw.map((k) => String(k).trim().toLowerCase()).filter(Boolean);
        const normalizedSorted = [...norm].sort((a, b) => a.localeCompare(b));
        // Clé = mots-clés réels (sans forcer "practice"), sinon une ligne par session (évite tout fusionner en une carte).
        const key =
            normalizedSorted.length > 0
                ? normalizedSorted.join('|')
                : `__session__|${s['make-up_session_id'] || 'unknown'}`;

        const nb = s.content['make-up_questions_nb'] || 0;
        if (!map.has(key)) {
            const displayKw =
                rawKw.length > 0
                    ? rawKw.map(String)
                    : ['(no keywords)', String(s['make-up_session_id'] || '').slice(-6) || 'session'];
            const forSearch =
                normalizedSorted.length > 0
                    ? rawKw.map((k) => String(k).trim()).filter(Boolean).slice(0, 8)
                    : [s.metadata?.task, s.metadata?.domain].map((x) => String(x || '').trim()).filter(Boolean);
            const searchTags = forSearch.length > 0 ? forSearch : ['practice'];
            map.set(key, {
                key,
                keywords: displayKw,
                searchTags,
                domain: s.metadata?.domain || '',
                scoreSum: 0,
                sessionCount: 0,
            });
        }
        const row = map.get(key);
        row.scoreSum += nb;
        row.sessionCount = (row.sessionCount || 0) + 1;
        if (!row.domain && s.metadata?.domain) {
            row.domain = s.metadata.domain;
        }
    }

    return Array.from(map.values())
        .sort((a, b) => b.scoreSum - a.scoreSum)
        .map((row) => ({
            ...row,
            avgScore: row.sessionCount > 0 ? Number((row.scoreSum / row.sessionCount).toFixed(2)) : row.scoreSum,
            barScore: Math.min(5, row.scoreSum),
            isCritical: row.scoreSum >= 5,
        }));
}

const FLASH_DASH_VIGNETTE_KEY = (studentId) => `flashcard_dashboard_vignettes_${studentId || 'anonymous_user'}`;

/** Dernières notations de compréhension (max 4) pour le dashboard. */
export function pushFlashcardDashboardVignette(studentId, { id, label }) {
    try {
        const key = FLASH_DASH_VIGNETTE_KEY(studentId);
        const prev = JSON.parse(localStorage.getItem(key) || '[]');
        if (!Array.isArray(prev)) return;
        prev.unshift({
            id: String(id || ''),
            label: String(label || ''),
            at: Date.now(),
        });
        localStorage.setItem(key, JSON.stringify(prev.slice(0, 4)));
    } catch (_) {
        /* ignore */
    }
}

export function loadFlashcardDashboardVignettes(studentId) {
    try {
        const raw = localStorage.getItem(FLASH_DASH_VIGNETTE_KEY(studentId));
        const arr = JSON.parse(raw || '[]');
        return Array.isArray(arr) ? arr : [];
    } catch {
        return [];
    }
}

const FLASHCARD_MAKEUP_NB_CAP = 20;

/**
 * Flash Card / Tips (spec) : cumul score (-2 / -1 / +2) → make-up_questions_nb = max(0, -cumul), plafonné.
 * Session marquée content.flashcard_tips pour ne pas fusionner avec les erreurs QCM classiques.
 */
export function recordFlashcardTipsComprehension(
    studentId,
    { sourceQuestionId, scoreDelta, keywords, domain, task }
) {
    if (!sourceQuestionId) return null;
    const sid = studentId || 'anonymous_user';
    const sessions = loadSessions(sid);
    const triple = (keywords || []).slice(0, 3);
    while (triple.length < 3) triple.push('practice');
    const delta = Number(scoreDelta);
    if (!Number.isFinite(delta)) return null;

    const idx = sessions.findIndex(
        (s) =>
            String(s.source_question_id) === String(sourceQuestionId) &&
            s.content &&
            s.content.flashcard_tips === true
    );

    if (idx >= 0) {
        const row = sessions[idx];
        const prevScore = Number(row.content.flashcard_cumulative_score || 0);
        const cumulative = prevScore + delta;
        row.content.flashcard_cumulative_score = cumulative;
        const nb = Math.min(
            FLASHCARD_MAKEUP_NB_CAP,
            Math.max(0, Math.floor(-cumulative))
        );
        row.content['make-up_questions_nb'] = nb;
        row.content.keywords = triple;
        row.metadata = row.metadata || {};
        row.metadata.domain = domain || row.metadata.domain || '';
        row.metadata.task = task || row.metadata.task || '';
        row.metadata.question_type = 'FlashCard_Tips';
        if (row.content.initial_makeup_questions_nb == null) {
            row.content.initial_makeup_questions_nb = nb;
        }
        if (nb <= 0 && cumulative >= 0) {
            sessions.splice(idx, 1);
        }
        saveSessions(sid, sessions);
        return sessions[idx] || null;
    }

    const cumulative = delta;
    const nb = Math.min(FLASHCARD_MAKEUP_NB_CAP, Math.max(0, Math.floor(-cumulative)));
    if (nb <= 0 && cumulative >= 0) {
        saveSessions(sid, sessions);
        return null;
    }

    const createdAt = new Date().toISOString();
    const session = {
        'make-up_session_id': generateMakeupSessionId(),
        created_at: createdAt,
        student_id: sid,
        source_question_id: String(sourceQuestionId),
        metadata: {
            domain: domain || '',
            task: task || '',
            created_at: createdAt,
            question_type: 'FlashCard_Tips',
        },
        content: {
            keywords: triple,
            'make-up_questions_nb': nb,
            initial_makeup_questions_nb: nb,
            flashcard_tips: true,
            flashcard_cumulative_score: cumulative,
        },
    };
    sessions.push(session);
    saveSessions(sid, sessions);
    return session;
}
